import { useState } from "react";
import axios from "axios";
import { Check, Sparkles, Zap, Crown, ArrowRight, Loader2 } from "lucide-react";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PLAN_CONFIG = {
  free: { icon: Sparkles, gradient: "from-zinc-600 to-zinc-800", accent: "text-zinc-400", badge: null },
  starter: { icon: Zap, gradient: "from-[#00F0FF] to-[#0070FF]", accent: "text-[#00F0FF]", badge: null },
  professional: { icon: Crown, gradient: "from-[#7000FF] to-[#00F0FF]", accent: "text-[#7000FF]", badge: "POPULAR" },
  enterprise: { icon: Crown, gradient: "from-amber-400 to-amber-600", accent: "text-amber-400", badge: null }
};

export default function SubscriptionPlans({ plans, currentPlan, user, onSubscribed }) {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const validateCoupon = async (planId) => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError("");
    setCouponResult(null);
    try {
      const res = await axios.post(`${API}/subscriptions/validate-coupon`, {
        code: couponCode.trim(),
        plan_id: planId,
        billing_cycle: billingCycle
      });
      setCouponResult(res.data);
    } catch (err) {
      setCouponError(err.response?.data?.detail || "Invalid coupon");
      setCouponResult(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      startGoogleLogin('/ai-tools/call-analyzer');
      return;
    }

    setLoading(planId);
    try {
      // Get auth token from sessionStorage
      const authHeaders = {};
      try {
        const userData = JSON.parse(sessionStorage.getItem("user_data") || "{}");
        if (userData.session_token) authHeaders.Authorization = `Bearer ${userData.session_token}`;
      } catch {}

      const res = await axios.post(`${API}/subscriptions/create-order`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        coupon_code: couponResult?.valid ? couponCode.trim() : null,
        origin_url: window.location.origin
      }, { headers: authHeaders });

      // Load Cashfree SDK and redirect to payment
      const { order_id, payment_session_id } = res.data;

      if (window.Cashfree) {
        const cashfree = window.Cashfree({ mode: "production" });
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_self"
        });
      } else {
        // Fallback: load Cashfree JS dynamically
        const script = document.createElement("script");
        script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        script.onload = () => {
          const cashfree = window.Cashfree({ mode: "production" });
          cashfree.checkout({
            paymentSessionId: payment_session_id,
            redirectTarget: "_self"
          });
        };
        document.body.appendChild(script);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create order";
      alert(msg);
    } finally {
      setLoading(null);
    }
  };

  const getPrice = (plan) => {
    if (plan.id === "free") return { amount: 0, period: "", original: 0 };
    const original = billingCycle === "annual" ? plan.annual_price : plan.monthly_price;
    let amount = original;
    if (couponResult?.valid) {
      amount = Math.round(original * (1 - couponResult.discount_percentage / 100));
    }
    const period = billingCycle === "annual" ? "/year" : "/month";
    return { amount, period, original };
  };

  const getAnnualSavings = (plan) => {
    if (plan.id === "free") return 0;
    return (plan.monthly_price * 12) - plan.annual_price;
  };

  return (
    <div data-testid="subscription-plans">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <span className={`text-sm ${billingCycle === "monthly" ? "text-white font-semibold" : "text-zinc-500"}`}>Monthly</span>
        <button
          onClick={() => setBillingCycle(b => b === "monthly" ? "annual" : "monthly")}
          className="relative w-14 h-7 rounded-full bg-zinc-800 border border-zinc-700 transition-colors"
          data-testid="billing-toggle"
        >
          <div className={`absolute top-0.5 w-6 h-6 rounded-full transition-all duration-300 ${
            billingCycle === "annual" ? "left-7 bg-[#00F0FF]" : "left-0.5 bg-zinc-500"
          }`} />
        </button>
        <span className={`text-sm ${billingCycle === "annual" ? "text-white font-semibold" : "text-zinc-500"}`}>
          Annual <span className="text-emerald-400 text-xs">(Save 2 months)</span>
        </span>
      </div>

      {/* Coupon Code Input */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="flex items-center bg-[#0d0d14] border border-zinc-800 rounded-xl overflow-hidden">
          <input
            type="text"
            placeholder="Coupon code"
            value={couponCode}
            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); setCouponError(""); }}
            className="px-4 py-2 bg-transparent text-white text-sm placeholder-zinc-600 focus:outline-none w-36"
            data-testid="coupon-input"
          />
          <button
            onClick={() => validateCoupon("starter")}
            disabled={!couponCode.trim() || validatingCoupon}
            className="px-4 py-2 text-sm font-medium text-[#00F0FF] hover:text-white transition-colors disabled:opacity-40 border-l border-zinc-800"
            data-testid="apply-coupon-btn"
          >
            {validatingCoupon ? "..." : "Apply"}
          </button>
        </div>
        {couponResult?.valid && (
          <span className="text-emerald-400 text-xs font-medium">{couponResult.discount_percentage}% off applied!</span>
        )}
        {couponError && <span className="text-red-400 text-xs">{couponError}</span>}
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(plans || []).map(plan => {
          const config = PLAN_CONFIG[plan.id] || PLAN_CONFIG.free;
          const Icon = config.icon;
          const price = getPrice(plan);
          const isCurrentPlan = currentPlan === plan.id;
          const savings = billingCycle === "annual" ? getAnnualSavings(plan) : 0;

          return (
            <div
              key={plan.id}
              className={`relative bg-[#0d0d14] rounded-2xl border transition-all ${
                config.badge ? "border-[#7000FF]/50 ring-1 ring-[#7000FF]/20" : "border-zinc-800/60"
              } ${isCurrentPlan ? "ring-2 ring-[#00F0FF]/40" : ""}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {/* Badge */}
              {config.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7000FF] text-white text-[10px] font-bold px-3 py-0.5 rounded-full">
                  {config.badge}
                </div>
              )}

              <div className="p-5">
                {/* Plan header */}
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-3`}>
                  <Icon size={20} className="text-white" />
                </div>
                <h3 className="text-white font-bold text-lg font-outfit">{plan.name}</h3>

                {/* Price */}
                <div className="mt-3 mb-4">
                  {plan.id === "free" ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold text-white">Free</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-zinc-500 text-lg">&#8377;</span>
                        <span className="text-3xl font-extrabold text-white">{price.amount.toLocaleString()}</span>
                        <span className="text-zinc-500 text-sm">{price.period}</span>
                      </div>
                      {couponResult?.valid && price.original !== price.amount && (
                        <p className="text-zinc-500 text-xs mt-0.5 line-through">&#8377;{price.original.toLocaleString()}{price.period}</p>
                      )}
                      {savings > 0 && !couponResult?.valid && (
                        <p className="text-emerald-400 text-xs mt-1">Save &#8377;{savings.toLocaleString()}/year</p>
                      )}
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-5">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className={`${config.accent} flex-shrink-0 mt-0.5`} />
                      <span className="text-zinc-400 text-xs">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {plan.id === "free" ? (
                  <div className="text-center py-2">
                    <span className="text-zinc-600 text-xs">{isCurrentPlan ? "Current plan" : "Default"}</span>
                  </div>
                ) : isCurrentPlan ? (
                  <div className="w-full py-2.5 border border-[#00F0FF]/40 text-[#00F0FF] text-sm font-bold rounded-xl text-center">
                    Current Plan
                  </div>
                ) : (
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={!!loading}
                    className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                      config.badge
                        ? "bg-gradient-to-r from-[#7000FF] to-[#00F0FF] text-white hover:opacity-90"
                        : "bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700"
                    } disabled:opacity-50`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {loading === plan.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>Subscribe <ArrowRight size={14} /></>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
